import { createAdminGuard, createAuthGuard, createGuestGuard, type AuthSession } from '@hb/auth-client';
import { createRouter, createWebHistory, type Router, type RouterHistory } from 'vue-router';

/**
 * Routes, and which of them need a session.
 *
 * `createWebHistory` rather than hash history: these are real, linkable URLs
 * and a verification email points at one. Apache has to answer every path with
 * `index.html` for that to work - see `deploy/spa.htaccess.template`.
 *
 * The history is a parameter only so a test can drive the real routes and the
 * real guards with `createMemoryHistory()` instead of a browser.
 */
export function createAppRouter(session: AuthSession, history: RouterHistory = createWebHistory()): Router {
  const requireSession = createAuthGuard(session, { signIn: '/login' });
  const requireAdmin = createAdminGuard(session, { signIn: '/login', forbidden: '/account' });
  // Signing in again on top of a live session would open a second one for no
  // reason, so the screens that do it are for guests only.
  const guestOnly = createGuestGuard(session, { home: '/account' });

  return createRouter({
    history,
    routes: [
      // The front door, for guests; a session goes on to its account. The bare
      // domain redirects here too.
      { path: '/', name: 'welcome', component: () => import('./views/WelcomeView.vue'), beforeEnter: guestOnly },

      {
        path: '/account',
        name: 'account',
        component: () => import('./views/AccountView.vue'),
        beforeEnter: requireSession,
      },
      {
        path: '/account/delete',
        name: 'delete-account',
        component: () => import('./views/DeleteAccountView.vue'),
        beforeEnter: requireSession,
      },
      // Where the deletion form leads. For guests, because by then the session
      // that asked has ended - and an account in its last hour cannot open one.
      {
        path: '/account/deletion-scheduled',
        name: 'deletion-scheduled',
        component: () => import('./views/DeletionScheduledView.vue'),
        beforeEnter: guestOnly,
      },
      // Neither of these is guarded. The first is the public page a store
      // listing points at, for anybody; the second is the link in the email,
      // which works whoever else happens to be signed in in this browser.
      { path: '/delete-account', name: 'delete-account-info', component: () => import('./views/DeleteAccountInfoView.vue') },
      { path: '/keep-account', name: 'keep-account', component: () => import('./views/KeepAccountView.vue') },
      {
        path: '/admin',
        name: 'admin',
        component: () => import('./views/AdminView.vue'),
        beforeEnter: requireAdmin,
      },

      { path: '/login', name: 'login', component: () => import('./views/LoginView.vue'), beforeEnter: guestOnly },
      { path: '/login/totp', name: 'login-totp', component: () => import('./views/TotpChallengeView.vue'), beforeEnter: guestOnly },

      { path: '/signup', name: 'signup', component: () => import('./views/SignupView.vue'), beforeEnter: guestOnly },
      { path: '/signup/check-email', name: 'check-email', component: () => import('./views/CheckEmailView.vue'), beforeEnter: guestOnly },

      // The address in the verification email. The token arrives in the query
      // because an email link is the only way it can, and it is single-use.
      { path: '/verify-email', name: 'verify-email', component: () => import('./views/VerifyEmailView.vue'), beforeEnter: guestOnly },
      { path: '/enrol', name: 'enrol', component: () => import('./views/EnrolView.vue'), beforeEnter: guestOnly },

      { path: '/forgot-password', name: 'forgot-password', component: () => import('./views/ForgotPasswordView.vue'), beforeEnter: guestOnly },
      { path: '/reset-password', name: 'reset-password', component: () => import('./views/ResetPasswordView.vue'), beforeEnter: guestOnly },

      // No 404 screen: every path here is one of ours, so an unknown one is a
      // stale bookmark or a typo, and the account page is where they meant to be.
      { path: '/:pathMatch(.*)*', redirect: '/account' },
    ],
    scrollBehavior: () => ({ top: 0 }),
  });
}
